import React, { useEffect, useState } from 'react';
import { AlertTriangle, Loader, Trash2 } from 'lucide-react';

const HardDeleteConfirmDialog = ({ isOpen, onClose, onConfirm, needTitle }) => {
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    setConfirmText('');
  }, [isOpen, needTitle]);

  const handleConfirm = async () => {
    if (confirmText !== 'DELETE') return;

    setIsDeleting(true);
    try {
      await onConfirm();
      onClose();
    } catch (error) {
      console.error('Error during hard delete:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" role="dialog" aria-modal="true" aria-labelledby="hard-delete-title">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex items-center mb-4">
          <AlertTriangle className="h-6 w-6 text-red-500 mr-2" />
          <h3 id="hard-delete-title" className="text-lg font-semibold text-gray-900">
            Permanent Deletion Warning
          </h3>
        </div>

        <div className="mb-4">
          <p className="text-sm text-gray-600 mb-2">
            You are about to <strong>permanently delete</strong> the need:
          </p>
          <p className="text-sm font-medium text-gray-900 bg-gray-100 p-2 rounded">
            "{needTitle}"
          </p>
        </div>

        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
          <p className="text-sm text-red-800 mb-2">
            <strong>This action will permanently delete:</strong>
          </p>
          <ul className="text-xs text-red-700 list-disc list-inside space-y-1">
            <li>The need record</li>
            <li>All steps associated with this need</li>
            <li>All step details and history</li>
            <li>All scoring relationships</li>
            <li>All relationships to/from this need</li>
          </ul>
          <p className="text-xs text-red-700 mt-2">
            <strong>Note:</strong> Candidates will NOT be deleted, only their relationships to this need.
          </p>
          <p className="text-xs text-red-700 mt-2">
            Any JobConnect publication must be deleted and fully synchronized first.
          </p>
        </div>

        <div className="mb-4">
          <label htmlFor="hard-delete-confirm" className="block text-sm font-medium text-gray-700 mb-2">
            Type "DELETE" to confirm:
          </label>
          <input
            id="hard-delete-confirm"
            type="text"
            value={confirmText}
            onChange={(event) => setConfirmText(event.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
            placeholder="DELETE"
          />
        </div>

        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={confirmText !== 'DELETE' || isDeleting}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-md flex items-center"
          >
            {isDeleting ? (
              <>
                <Loader className="animate-spin h-4 w-4 mr-2" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                Permanently Delete
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HardDeleteConfirmDialog;
